import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { useFetchTradingInformationTabDataQuery } from '@/store/fundDetails/services/fundDetailsApi';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import { useMemo } from 'react';

const dictionaryItems = {
  bloombergCode: 'fundTradingInfoBloombergCode',
  bloombergTicker: 'fundCodesBloomberg',
  currency: 'fundDetailsCurrency',
  cusip: 'fundCodesCusip',
  exchangeCode: 'fundTradingInfoExchangeCode',
  reutersCode: 'fundTradingInfoReutersCode',
  ric: 'fundCodesRic',
  sedol: 'fundCodesSedol',
  tradingHours: 'fundTradingInfoTradingHours',
};

const FundDetailsTradingInformationTab = () => {
  const { t } = useI18n();
  const { getErrorDictionary } = useHelpers();
  const { shareClass, language, countryCode, correlationFundRangeId, site } = usePayload();
  const { data, error, isLoading } = useFetchTradingInformationTabDataQuery(
    {
      shareClass,
      language,
      countryCode,
      correlationFundRangeId,
      site,
    },
    {
      skip: !shareClass,
    }
  );

  const theadData = useMemo(() => {
    if (data) {
      const [firstRow] = data;
      return firstRow ? Object.keys(firstRow).filter((key) => key !== 'name') : [];
    }
  }, [data]);

  const tbodyData = useMemo(() => {
    return data
      ? data.map((row) => ({
          header: Object.entries(row).find(([key]) => key === 'name')[1],
          data: Object.entries(row)
            .filter(([key]) => key !== 'name')
            .map(([, value]) => value || t('notAvailableShortMessage')),
        }))
      : [];
  }, [data]);

  const ComponentContent = () => {
    return (
      <div className="abrdn-table">
        <table aria-label={t('fundDetailsTabsTradingInformation')} className="minimal">
          <thead>
            <tr>
              <th scope="col">
                <span className="visually-hidden">{t('fundTradingInfoExchange')}</span>
              </th>
              {theadData.map((key) => (
                <th key={key} scope="col">
                  {t(dictionaryItems[key])}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tbodyData.map((row, index) => (
              <tr key={index}>
                <th scope="row">{row.header}</th>
                {row.data.map((data) => (
                  <td key={data}>{data}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <FundDetailsTabRow fullWidth className="mt-4">
      {isLoading ? (
        <FundDetailsTabSkeleton />
      ) : error ? (
        <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
      ) : data ? (
        <ComponentContent />
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}
    </FundDetailsTabRow>
  );
};

export default FundDetailsTradingInformationTab;
